import type { SlackConnectSettingsType } from "@productlane/zero/types"

import { GROUPING_OPTIONS } from "./slack-grouping"

export const WORKSPACE_DEFAULT = "default"

// Inbox off already means "no threads from this channel", so manual push only is left out here.
export const CHANNEL_GROUPING_OPTIONS = [
	{ value: WORKSPACE_DEFAULT, label: "Workspace default" },
	...GROUPING_OPTIONS.filter((option) => option.value !== "MANUAL"),
]

export const NEW_CHANNEL_OPTIONS = [
	{ value: "on", label: "Become inboxes" },
	{ value: "off", label: "Stay off until turned on" },
]

/** How many rows a group renders before "Show more", so a 1,000-channel workspace stays fast. */
export const CHANNEL_PAGE_SIZE = 100

interface ChannelGrouping {
	type?: string | null
	groupingWindowMinutes?: number | null
}

export function channelGroupingValue(channel: ChannelGrouping): string {
	if (!channel.type) return WORKSPACE_DEFAULT
	const value =
		channel.type === "SINGLE"
			? `SINGLE:${channel.groupingWindowMinutes ?? 30}`
			: channel.type
	return CHANNEL_GROUPING_OPTIONS.some((option) => option.value === value)
		? value
		: WORKSPACE_DEFAULT
}

export function parseChannelGrouping(value: string): {
	type: SlackConnectSettingsType | null
	groupingWindowMinutes: number | null
} {
	if (value === WORKSPACE_DEFAULT)
		return { type: null, groupingWindowMinutes: null }
	const [type, window] = value.split(":")
	return {
		type: type as SlackConnectSettingsType,
		groupingWindowMinutes: window ? Number(window) : null,
	}
}

export function channelGroupingLabel(channel: ChannelGrouping): string | null {
	const value = channelGroupingValue(channel)
	if (value === WORKSPACE_DEFAULT) return null
	return (
		CHANNEL_GROUPING_OPTIONS.find((option) => option.value === value)?.label ??
		null
	)
}

export function filterChannels<T extends { name: string; inbox: boolean }>(
	channels: readonly T[],
	query: string,
): { inbox: T[]; other: T[] } {
	const q = query.trim().toLowerCase()
	const visible = q
		? channels.filter((channel) => channel.name.toLowerCase().includes(q))
		: channels
	return {
		inbox: visible.filter((channel) => channel.inbox),
		other: visible.filter((channel) => !channel.inbox),
	}
}

/** "Set all" skips the notification channel, which serves notifications rather than the inbox. */
export function bulkEditableIds(
	channels: readonly { id: string; channelId: string }[],
	notificationChannelId: string | null | undefined,
): string[] {
	return channels
		.filter((channel) => channel.channelId !== notificationChannelId)
		.map((channel) => channel.id)
}

export function channelSummary(inbox: number, other: number): string {
	if (inbox === 0 && other === 0) return "No channels yet"
	const inboxLabel = `${inbox} inbox ${inbox === 1 ? "channel" : "channels"}`
	return `${inboxLabel}, ${other} ${other === 1 ? "other" : "others"}`
}

/** "https://linear.slack.com/" becomes "linear.slack.com". */
export function slackDomain(teamUrl: string): string {
	return teamUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")
}

export type ChannelSortKey = "name" | "grouping" | "tag" | "assignee"
export type ChannelSort = `${ChannelSortKey}-${"asc" | "desc"}`
export const DEFAULT_CHANNEL_SORT: ChannelSort = "name-asc"

interface SortableChannel extends ChannelGrouping {
	name: string
	tag?: { name: string } | null
	assignee?: { name?: string | null; email?: string | null } | null
}

// Grouping sorts by window length, shortest first, not by its label.
const GROUPING_RANK = new Map(
	CHANNEL_GROUPING_OPTIONS.map((option, index) => [option.value, index]),
)

function sortValue(
	channel: SortableChannel,
	key: ChannelSortKey,
): string | number | null {
	if (key === "name") return channel.name.toLowerCase()
	if (key === "grouping") {
		const value = channelGroupingValue(channel)
		return value === WORKSPACE_DEFAULT
			? null
			: (GROUPING_RANK.get(value) ?? null)
	}
	if (key === "tag") return channel.tag?.name.toLowerCase() ?? null
	const assignee = channel.assignee?.name || channel.assignee?.email
	return assignee ? assignee.toLowerCase() : null
}

/** Sorts by a column; empty cells stay at the bottom either way, ties go by name. */
export function sortChannels<T extends SortableChannel>(
	channels: readonly T[],
	sort: ChannelSort,
): T[] {
	const [key, direction] = sort.split("-") as [ChannelSortKey, "asc" | "desc"]
	const sign = direction === "asc" ? 1 : -1
	return [...channels].sort((a, b) => {
		const left = sortValue(a, key)
		const right = sortValue(b, key)
		if (left === null || right === null) {
			if (left !== right) return left === null ? 1 : -1
		} else if (left !== right) {
			return (left < right ? -1 : 1) * sign
		}
		return a.name.localeCompare(b.name)
	})
}

export const DISABLE_CHANNEL_VALUE = "__disable__"

/** How many channels a notification picker shows; typing searches the rest. */
export const CHANNEL_PICKER_LIMIT = 100

/** A picker re-reads Slack's channel list at most this often, so a channel made a minute ago shows up. */
export const CHANNEL_REFRESH_INTERVAL_MS = 60_000

/** "Disable" first while it matches the search, then the channels the query found. */
export function channelPickerOptions(
	channels: readonly { channelId: string; name: string }[],
	{
		search,
		excludeChannelId,
	}: { search: string; excludeChannelId?: string | null },
): { value: string; label: string }[] {
	const query = search.trim().toLowerCase()
	const disable =
		!query || "disable".includes(query)
			? [{ value: DISABLE_CHANNEL_VALUE, label: "Disable" }]
			: []
	return [
		...disable,
		...channels
			.filter((channel) => channel.channelId !== excludeChannelId)
			.map((channel) => ({ value: channel.channelId, label: channel.name })),
	]
}

/** The trigger's label: the saved channel can sit outside the loaded page, so it is looked up on its own. */
export function savedChannelLabel(
	saved: string | null | undefined,
	channel: { name: string } | undefined,
	loaded: boolean,
): string {
	if (!saved) return "Disable"
	if (channel) return channel.name
	return loaded ? "Unavailable channel" : ""
}

export function shouldRefreshChannels(
	lastRefreshAt: number | undefined,
	now: number,
): boolean {
	return (
		lastRefreshAt === undefined ||
		now - lastRefreshAt >= CHANNEL_REFRESH_INTERVAL_MS
	)
}
